import React, { useEffect, useCallback } from 'react';
import { Modal } from '@mantine/core';
import { TbCheck } from 'react-icons/tb';
import {
  ComparisonSession,
  getPivotIndex,
  getEstimatedComparisonsRemaining,
  UrgencyBand,
} from '../utils/stackRanking';
import './ComparisonModal.css';

interface TaskForComparison {
  id: string;
  title: string;
  categoryName: string;
  createdAt: number;
}

interface CelebrationData {
  score: number;
  band: UrgencyBand;
  rank: number;
  totalInBand: number;
  taskTitle: string;
  taskId?: string;
}

interface ComparisonModalProps {
  opened: boolean;
  onClose: () => void;
  session: ComparisonSession | null;
  newTask: TaskForComparison | null;
  candidateTasks: TaskForComparison[];
  onChoice: (choice: 'left' | 'right' | 'skip' | 'too-tough') => void;
  onUndo: () => void;
  canUndo: boolean;
  comparisonNumber: number;
  celebration?: CelebrationData | null;
  onMarkDone?: (taskId: string) => void;
  rerankAllMode?: boolean;
  rerankAllTotal?: number;
  rerankAllCompleted?: number;
  rerankAllComplete?: boolean;
  comparisonsSkipped?: number;
  // Merge-sort mode props
  mergeSortMode?: boolean;
  mergeSortLeftTask?: TaskForComparison | null;
  mergeSortRightTask?: TaskForComparison | null;
  mergeSortCurrentComparison?: number;
  mergeSortEstimatedTotal?: number;
}

function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);

  if (weeks > 0) return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (minutes > 0) return `${minutes} min${minutes > 1 ? 's' : ''} ago`;
  return 'just now';
}

const BAND_COLORS: Record<UrgencyBand, string> = {
  high: '#d32f2f',
  medium: '#f57c00',
  low: '#1976d2',
};

const BAND_LABELS: Record<UrgencyBand, string> = {
  high: 'High Priority',
  medium: 'Medium Priority',
  low: 'Low Priority',
};

export const ComparisonModal: React.FC<ComparisonModalProps> = ({
  opened,
  onClose,
  session,
  newTask,
  candidateTasks,
  onChoice,
  onUndo,
  canUndo,
  comparisonNumber,
  celebration,
  onMarkDone,
  rerankAllMode,
  rerankAllTotal = 0,
  rerankAllCompleted = 0,
  rerankAllComplete,
  comparisonsSkipped = 0,
  mergeSortMode = false,
  mergeSortLeftTask,
  mergeSortRightTask,
  mergeSortCurrentComparison = 0,
  mergeSortEstimatedTotal = 0,
}) => {
  // For standard binary search mode
  const pivotIndex = session ? getPivotIndex(session) : 0;
  const pivotTask = session ? candidateTasks.find(
    t => t.id === session.candidates[pivotIndex]
  ) : null;
  const estimatedTotal = session
    ? comparisonNumber + getEstimatedComparisonsRemaining(session)
    : 0;

  // Determine which tasks to display based on mode
  const leftTask = mergeSortMode ? mergeSortLeftTask : newTask;
  const rightTask = mergeSortMode ? mergeSortRightTask : pivotTask;
  const displayCurrentComparison = mergeSortMode ? mergeSortCurrentComparison : comparisonNumber;
  const displayEstimatedTotal = mergeSortMode ? mergeSortEstimatedTotal : estimatedTotal;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!opened || celebration) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          onChoice('left');
          break;
        case 'ArrowRight':
          e.preventDefault();
          onChoice('right');
          break;
        case 'ArrowUp':
          e.preventDefault();
          onChoice('skip');
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (canUndo) onUndo();
          break;
      }
    },
    [opened, celebration, onChoice, onUndo, canUndo]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Show completion view for re-rank all
  if (rerankAllComplete) {
    return (
      <Modal
        opened={opened}
        onClose={onClose}
        title=""
        size="md"
        centered
        withCloseButton={false}
        classNames={{
          content: 'comparison-modal-content celebration-modal-content',
          header: 'comparison-modal-header',
          body: 'comparison-modal-body',
        }}
      >
        <div className="rerank-all-complete">
          <div className="complete-icon">
            <TbCheck size={48} />
          </div>
          <h2>All {rerankAllTotal} tasks re-ranked!</h2>
          <p className="complete-subtitle">Your task priorities are now up to date.</p>
          {comparisonsSkipped > 0 && (
            <p className="complete-skipped">
              {comparisonsSkipped} comparison{comparisonsSkipped !== 1 ? 's' : ''} skipped using your comparison history
            </p>
          )}
          <button className="complete-done-button" onClick={onClose}>
            Done
          </button>
        </div>
      </Modal>
    );
  }

  // Show celebration view
  if (celebration) {
    const positionText = celebration.rank === 1
      ? '🥇 Top of the stack!'
      : celebration.rank === celebration.totalInBand
        ? `#${celebration.rank} in ${BAND_LABELS[celebration.band].toLowerCase()}`
        : `#${celebration.rank} of ${celebration.totalInBand} in ${BAND_LABELS[celebration.band].toLowerCase()}`;

    return (
      <Modal
        opened={opened}
        onClose={onClose}
        title=""
        size="md"
        centered
        withCloseButton={false}
        classNames={{
          content: 'comparison-modal-content celebration-modal-content',
          header: 'comparison-modal-header',
          body: 'comparison-modal-body',
        }}
      >
        <div className="celebration-container">
          <div className="celebration-confetti" aria-hidden="true">
            {[...Array(12)].map((_, i) => (
              <span key={i} className="confetti-piece" style={{ '--i': i } as React.CSSProperties} />
            ))}
          </div>

          <div className="celebration-content">
            <div
              className="celebration-score-ring"
              style={{ '--band-color': BAND_COLORS[celebration.band] } as React.CSSProperties}
            >
              <span className="celebration-score">{celebration.score.toFixed(1)}</span>
            </div>

            <div
              className="celebration-band-label"
              style={{ color: BAND_COLORS[celebration.band] }}
            >
              {BAND_LABELS[celebration.band]}
            </div>

            <div className="celebration-position">
              {positionText}
            </div>

            <div className="celebration-task-title">
              {celebration.taskTitle}
            </div>

            {rerankAllMode && (
              <div className="rerank-all-progress celebration-progress">
                <div className="progress-text">
                  Task {rerankAllCompleted + 1} of {rerankAllTotal}
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${((rerankAllCompleted + 1) / rerankAllTotal) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  // For standard mode, require session; for merge-sort mode, require the task pair
  if (mergeSortMode) {
    if (!leftTask || !rightTask) return null;
  } else {
    if (!session || !newTask || !pivotTask) return null;
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title=""
      size="lg"
      centered
      withCloseButton={true}
      classNames={{
        content: 'comparison-modal-content',
        header: 'comparison-modal-header',
        body: 'comparison-modal-body',
      }}
    >
      <div className="comparison-modal">
        {rerankAllMode && (
          <div className="rerank-all-progress">
            <div className="progress-header">
              Ranking {rerankAllTotal} tasks
            </div>
            <div className="progress-text">
              {mergeSortMode
                ? `Comparison ${displayCurrentComparison} of ~${displayEstimatedTotal}`
                : `Task ${rerankAllCompleted + 1} of ${rerankAllTotal}`
              }
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${mergeSortMode
                    ? (displayCurrentComparison / displayEstimatedTotal) * 100
                    : (rerankAllCompleted / rerankAllTotal) * 100
                  }%`
                }}
              />
            </div>
            {mergeSortMode && comparisonsSkipped > 0 && (
              <div className="progress-skipped">
                {comparisonsSkipped} skipped via history
              </div>
            )}
          </div>
        )}

        {!rerankAllMode && (
          <div className="rerank-all-progress">
            <div className="progress-text">
              Comparison {displayCurrentComparison} of ~{displayEstimatedTotal}
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${(displayCurrentComparison / Math.max(displayEstimatedTotal, 1)) * 100}%`
                }}
              />
            </div>
          </div>
        )}

        <h2 className="comparison-question">Which task needs to be finished first?</h2>

        <div className="comparison-cards">
          <div className="comparison-card-wrapper">
            <button
              className="comparison-card"
              onClick={() => onChoice('left')}
              type="button"
            >
              <div className="comparison-card-title">{leftTask!.title}</div>
              <div className="comparison-card-category">{leftTask!.categoryName}</div>
              <div className="comparison-card-date">
                {formatRelativeTime(leftTask!.createdAt)}
              </div>
            </button>
            {onMarkDone && (
              <button
                className="comparison-mark-done"
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkDone(leftTask!.id);
                }}
                type="button"
              >
                <TbCheck /> Mark done
              </button>
            )}
          </div>

          <div className="comparison-vs-badge">vs</div>

          <div className="comparison-card-wrapper">
            <button
              className="comparison-card"
              onClick={() => onChoice('right')}
              type="button"
            >
              <div className="comparison-card-title">{rightTask!.title}</div>
              <div className="comparison-card-category">{rightTask!.categoryName}</div>
              <div className="comparison-card-date">
                {formatRelativeTime(rightTask!.createdAt)}
              </div>
            </button>
            {onMarkDone && (
              <button
                className="comparison-mark-done"
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkDone(rightTask!.id);
                }}
                type="button"
              >
                <TbCheck /> Mark done
              </button>
            )}
          </div>
        </div>

        <div className="comparison-keyboard-hint">
          ← Left wins | → Right wins | ↑ Skip (tie) | ↓ Undo
        </div>

        <div className="comparison-actions">
          <button
            className="comparison-action-button"
            onClick={() => canUndo && onUndo()}
            disabled={!canUndo}
            type="button"
          >
            ↩ Undo
          </button>

          <button
            className="comparison-action-button"
            onClick={() => onChoice('skip')}
            type="button"
          >
            Skip (Tie)
          </button>
        </div>

        <div className="comparison-progress">
          {displayCurrentComparison} of ~{displayEstimatedTotal}
        </div>
      </div>
    </Modal>
  );
};
